import { Dealer } from '@/types/dealer';
import { Coordinates } from '../types';
import { calculateDistance } from '@/lib/geolocation';

/**
 * Sort dealers by their spread value (best deals first)
 */
export const sortDealersBySpread = (dealers: Dealer[]): Dealer[] => {
  return [...dealers].sort((a, b) => {
    // First, ensure activated dealers come first
    if (a.activated !== b.activated) {
      return a.activated ? -1 : 1;
    }
    
    // Higher spread value = better deal for customer
    // e.g. -2% is better than -5%
    if (a.buySpread !== undefined && b.buySpread !== undefined) {
      return b.buySpread - a.buySpread;
    }
    
    // If no spread info, keep order
    return 0;
  });
};

/**
 * Sort dealers by rating (highest rating first)
 */
export const sortDealersByRating = (dealers: Dealer[]): Dealer[] => {
  return [...dealers].sort((a, b) => {
    // First, ensure activated dealers come first
    if (a.activated !== b.activated) {
      return a.activated ? -1 : 1;
    }
    
    // Higher rating is better
    const ratingA = a.rating || 0;
    const ratingB = b.rating || 0;
    
    return ratingB - ratingA;
  });
};

/**
 * Sort dealers by distance from user
 */
export const sortDealersByDistance = (
  dealers: Dealer[], 
  coordinates: Coordinates
): Dealer[] => {
  if (!coordinates || !coordinates.latitude || !coordinates.longitude) {
    return dealers;
  }
  
  return [...dealers].sort((a, b) => {
    if (!a.latitude || !a.longitude || !b.latitude || !b.longitude) {
      return 0;
    }
    
    const distanceA = calculateDistance(
      coordinates.latitude,
      coordinates.longitude,
      a.latitude,
      a.longitude
    );
    
    const distanceB = calculateDistance(
      coordinates.latitude,
      coordinates.longitude,
      b.latitude,
      b.longitude
    );
    
    return distanceA - distanceB;
  });
};

/**
 * Sort dealers by priority with distance-first approach when geolocation is available
 * This function implements the required sorting order for recommended dealers
 */
export const sortDealersByPriority = (
  dealers: Dealer[],
  coordinates?: Coordinates | null
): Dealer[] => {
  // Make a copy of the dealers array to avoid modifying the original
  let sortedDealers = [...dealers];
  
  // First, ensure activated dealers come first
  sortedDealers = sortedDealers.sort((a, b) => {
    if (a.activated !== b.activated) {
      return a.activated ? -1 : 1;
    }
    return 0;
  });
  
  // Check if we have valid coordinates for distance-based sorting
  const hasValidCoordinates = coordinates && 
    coordinates.latitude && 
    coordinates.longitude;
  
  if (hasValidCoordinates) {
    // DISTANCE-FIRST SORTING: When geolocation is available
    console.log('🎯 sortDealersByPriority: Using distance-first sorting with coordinates', {
      lat: coordinates.latitude,
      lng: coordinates.longitude,
      dealersCount: sortedDealers.length
    });
    
    sortedDealers = sortedDealers.sort((a, b) => {
      if (a.activated && b.activated) {
        // Primary criteria: Distance (when both dealers have coordinates)
        if (a.latitude && a.longitude && b.latitude && b.longitude) {
          const distanceA = calculateDistance(
            coordinates.latitude,
            coordinates.longitude,
            a.latitude,
            a.longitude
          );
          
          const distanceB = calculateDistance(
            coordinates.latitude,
            coordinates.longitude,
            b.latitude,
            b.longitude
          );
          
          // If distances are significantly different (>2km), prioritize distance
          const distanceDiff = Math.abs(distanceA - distanceB);
          if (distanceDiff > 2) {
            return distanceA - distanceB; // Closer distance is better
          }
          
          // If distances are similar, use spread as secondary criteria
          const spreadA = a.buySpread !== undefined ? a.buySpread : -100;
          const spreadB = b.buySpread !== undefined ? b.buySpread : -100;
          
          if (spreadA !== spreadB) {
            return spreadB - spreadA; // Higher spread is better
          }
          
          // If spreads are also equal, use rating as tertiary criteria
          const ratingA = a.rating || 0;
          const ratingB = b.rating || 0;
          
          if (ratingA !== ratingB) {
            return ratingB - ratingA; // Higher rating is better
          }
          
          // Final tie-breaker: distance
          return distanceA - distanceB;
        }
        
        // If one dealer doesn't have coordinates, prioritize the one that does
        if (a.latitude && a.longitude && (!b.latitude || !b.longitude)) {
          return -1;
        }
        if (b.latitude && b.longitude && (!a.latitude || !a.longitude)) {
          return 1;
        }
        
        // If neither has coordinates, fall back to spread-based sorting
        const spreadA = a.buySpread !== undefined ? a.buySpread : -100;
        const spreadB = b.buySpread !== undefined ? b.buySpread : -100;
        return spreadB - spreadA;
      }
      
      return 0;
    });
  } else {
    // FALLBACK SORTING: When geolocation is not available
    console.log('📍 sortDealersByPriority: Using spread-first sorting (no coordinates)');
    
    sortedDealers = sortedDealers.sort((a, b) => {
      if (a.activated && b.activated) {
        // Primary criteria: Spread
        const spreadA = a.buySpread !== undefined ? a.buySpread : -100;
        const spreadB = b.buySpread !== undefined ? b.buySpread : -100;
        
        if (spreadA !== spreadB) {
          return spreadB - spreadA; // Higher spread is better
        }
        
        // Secondary criteria: Rating
        const ratingA = a.rating || 0;
        const ratingB = b.rating || 0;
        
        if (ratingA !== ratingB) {
          return ratingB - ratingA; // Higher rating is better
        }
      }
      
      return 0;
    });
  }
  
  // Limit to maximum 3 dealers
  return sortedDealers.slice(0, 3);
};
